import { useEffect, useLayoutEffect, useRef, type RefObject } from 'react';

/** The focus is on the page, or on an element that holds `field`, such as the dialog that a focus scope moves it to when
 * an element goes away */
const isNowhere = (focused: Element | null, field: HTMLElement | null) =>
  !focused ||
  focused === document.body ||
  (field !== null && focused.contains(field));

/** While `isBusy`, the focus can leave `picker` without a blur that says so: a modal takes it, and a button that is
 * disabled while it works drops it to the page. When the busy time is over and `isOpen` still holds, the focus is looked
 * at once:
 * - When it is nowhere, it goes back to `field`.
 * - When it is on a control outside `picker`, the user has moved on, and `onLeave` is called.
 * - When it is in `picker`, nothing is done.
 *
 * When `isOpen` is over by then, the user has left already, and nothing is done.
 *
 * The check is made when the busy time ends, and not when `isOpen` does. A browser takes the focus off the control that
 * the user leaves before it lands on the next one, so the focus is nowhere while the blur is dispatched, and a refocus
 * at that moment would undo the move. */
export const useRefocusAfterBusy = ({
  isBusy,
  isOpen,
  field,
  picker,
  onLeave,
}: {
  isBusy: boolean;
  isOpen: boolean;
  /** The text field of the picker, which gets the focus back */
  field: RefObject<HTMLElement | null>;
  /** Everything of the picker that can hold the focus, the field included */
  picker: RefObject<HTMLElement | null>;
  onLeave: () => void;
}) => {
  // The check reads what holds when the busy time ends, and not when it began: the elements are taken from the last
  // commit, which is the one that ends the busy time
  const latest = useRef({
    isOpen,
    onLeave,
    field: null as HTMLElement | null,
    picker: null as HTMLElement | null,
  });
  useLayoutEffect(() => {
    latest.current = {
      isOpen,
      onLeave,
      field: field.current,
      picker: picker.current,
    };
  });

  useEffect(() => {
    if (!isBusy) return;
    return () => {
      const ending = latest.current;
      if (!ending.isOpen) return;
      const focused = document.activeElement;
      if (isNowhere(focused, ending.field)) {
        ending.field?.focus();
      } else if (!ending.picker?.contains(focused)) {
        ending.onLeave();
      }
    };
  }, [isBusy]);
};
